import { useState } from 'react'
import { useForm } from 'react-hook-form'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

const MembershipForm = ({ onSubmit, onCancel, membershipTypes, preSelectedMembership }) => {
  const [submitting, setSubmitting] = useState(false)
  const [householdMembers, setHouseholdMembers] = useState([{ firstName: '', lastName: '', email: '', dateOfBirth: '' }])
  const [apiError, setApiError] = useState(null)
  const [primaryDateOfBirth, setPrimaryDateOfBirth] = useState(null)

  // Determine default values based on preSelectedMembership
  const getDefaultMembershipType = () => {
    if (!preSelectedMembership) return 'household'
    // Use the membershipTypeValue if available (from questionnaire)
    if (preSelectedMembership.membershipTypeValue) {
      return preSelectedMembership.membershipTypeValue
    }
    // Fallback to title-based detection
    return preSelectedMembership.title.includes('Household') ? 'household' : 'single'
  }

  const getDefaultMemberStatus = () => {
    if (!preSelectedMembership) return 'returning'
    return preSelectedMembership.initiationFee ? 'new' : 'returning'
  }

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    trigger
  } = useForm({
    defaultValues: {
      membershipType: getDefaultMembershipType(),
      memberStatus: getDefaultMemberStatus(),
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: 'GA',
      zipCode: '30360'
    },
    mode: 'onBlur'
  })

  const membershipType = watch('membershipType')
  const memberStatus = watch('memberStatus')

  // Seniors who chose household keep senior pricing but can add household members
  const showHouseholdMembers = membershipType === 'household' || preSelectedMembership?.includeHousehold

  // Calculate total price based on selection
  const calculatePrice = () => {
    const type = membershipType === 'household' ? 'household' : 'singleSenior'
    // For single and senior, use the singleSenior pricing
    return membershipTypes[memberStatus][type].price
  }

  // Add household member
  const addHouseholdMember = () => {
    setHouseholdMembers([...householdMembers, { firstName: '', lastName: '', email: '', dateOfBirth: '' }])
  }

  // Remove household member
  const removeHouseholdMember = (index) => {
    if (householdMembers.length > 1) {
      setHouseholdMembers(householdMembers.filter((_, idx) => idx !== index))
    }
  }

  // Update household member
  const updateHouseholdMember = (index, field, value) => {
    const updated = [...householdMembers]
    updated[index][field] = value
    setHouseholdMembers(updated)
  }

  const onFormSubmit = (data) => {
    setSubmitting(true)
    setApiError(null)

    // Validate primary member date of birth
    if (!primaryDateOfBirth) {
      setApiError('Please enter your date of birth')
      setSubmitting(false)
      return
    }

    // Validate senior membership: primary member must be 65+ as of May 1 of the membership year
    if (data.membershipType === 'senior') {
      const membershipYear = new Date().getFullYear()
      const may1 = new Date(membershipYear, 4, 1) // May 1 of membership year
      const dob = new Date(primaryDateOfBirth)
      let age = may1.getFullYear() - dob.getFullYear()
      const monthDiff = may1.getMonth() - dob.getMonth()
      if (monthDiff < 0 || (monthDiff === 0 && may1.getDate() < dob.getDate())) {
        age--
      }
      if (age < 65) {
        setApiError(`Senior membership requires the primary member to be 65 or older as of May 1, ${membershipYear}. Based on your date of birth, you would be ${age}.`)
        setSubmitting(false)
        return
      }
    }

    // Validate household members if household membership type or senior with household
    if (showHouseholdMembers) {
      const invalidMembers = householdMembers.some(member =>
        !member.firstName?.trim() || !member.lastName?.trim() || !member.dateOfBirth?.trim()
      )

      if (invalidMembers) {
        setApiError('Please fill in all household member details (name and date of birth)')
        setSubmitting(false)
        return
      }

      // Validate email format for household members (only if email is provided)
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
      const invalidEmails = householdMembers.some(member =>
        member.email?.trim() && !emailRegex.test(member.email.trim())
      )

      if (invalidEmails) {
        setApiError('Please enter valid email addresses for household members')
        setSubmitting(false)
        return
      }
    }

    // Prepare submission data
    const submissionData = {
      ...data,
      primaryDateOfBirth: primaryDateOfBirth.toISOString().split('T')[0],
      // Include household members for household type or senior with household
      householdMembers: showHouseholdMembers ? householdMembers : [],
      totalAmount: calculatePrice(),
      submittedAt: new Date().toISOString()
    }

    // Pass form data to parent — member record will be created after payment succeeds
    onSubmit(submissionData)
    setSubmitting(false)
  }

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="container-custom max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onCancel}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-4 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Membership Info
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Membership Application</h1>
          <p className="text-lg text-gray-600">
            Complete the form below to apply for membership at Lockridge Forest Swim Tennis Club
          </p>
        </div>

        {/* Error Message */}
        {apiError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex">
              <svg className="w-6 h-6 text-red-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="font-semibold text-red-800 mb-1">Submission Error</h3>
                <p className="text-red-700">{apiError}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-8">
          {/* Membership Selection */}
          <div className="card p-8">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Membership Details</h2>

            {preSelectedMembership ? (
              /* Read-only display when membership is pre-selected from questionnaire */
              <div className="mb-6">
                <div className="p-6 bg-green-50 border-2 border-green-500 rounded-lg">
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-green-600 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">
                        {preSelectedMembership.title}
                      </h3>
                      <p className="text-gray-700 text-sm mb-3">
                        {preSelectedMembership.description}
                      </p>
                      <div className="flex items-baseline">
                        <span className="text-3xl font-bold text-green-600">
                          ${preSelectedMembership.price}
                        </span>
                        <span className="text-gray-700 ml-2">/year</span>
                      </div>
                      {preSelectedMembership.initiationFee && (
                        <div className="mt-2 text-sm text-gray-600">
                          <div>Annual Dues: ${preSelectedMembership.basePrice}</div>
                          <div>Initiation Fee: ${preSelectedMembership.initiationFee} (one-time)</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <p className="mt-3 text-sm text-gray-600 text-center">
                  This membership was selected based on your questionnaire answers.
                </p>
              </div>
            ) : (
              /* Original selection UI when no pre-selection */
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Membership Type */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Membership Type <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors has-[:checked]:border-blue-600 has-[:checked]:bg-blue-50">
                      <input
                        type="radio"
                        value="household"
                        {...register('membershipType', { required: 'Please select a membership type' })}
                        className="mt-1 mr-3"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">Household Membership</div>
                        <div className="text-sm text-gray-600">For families and households</div>
                      </div>
                    </label>
                    <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors has-[:checked]:border-blue-600 has-[:checked]:bg-blue-50">
                      <input
                        type="radio"
                        value="single"
                        {...register('membershipType', { required: 'Please select a membership type' })}
                        className="mt-1 mr-3"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">Single Membership</div>
                        <div className="text-sm text-gray-600">Individual (Under 65)</div>
                      </div>
                    </label>
                    <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors has-[:checked]:border-blue-600 has-[:checked]:bg-blue-50">
                      <input
                        type="radio"
                        value="senior"
                        {...register('membershipType', { required: 'Please select a membership type' })}
                        className="mt-1 mr-3"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">Senior Membership</div>
                        <div className="text-sm text-gray-600">Individual (65+)</div>
                      </div>
                    </label>
                  </div>
                  {errors.membershipType && (
                    <p className="mt-2 text-sm text-red-600">{errors.membershipType.message}</p>
                  )}
                </div>

                {/* Member Status */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Member Status <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors has-[:checked]:border-blue-600 has-[:checked]:bg-blue-50">
                      <input
                        type="radio"
                        value="returning"
                        {...register('memberStatus', { required: 'Please select your member status' })}
                        className="mt-1 mr-3"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">Returning Member</div>
                        <div className="text-sm text-gray-600">Previous member of LFST</div>
                      </div>
                    </label>
                    <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors has-[:checked]:border-blue-600 has-[:checked]:bg-blue-50">
                      <input
                        type="radio"
                        value="new"
                        {...register('memberStatus', { required: 'Please select your member status' })}
                        className="mt-1 mr-3"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">New Member</div>
                        <div className="text-sm text-gray-600">First time joining LFST</div>
                      </div>
                    </label>
                  </div>
                  {errors.memberStatus && (
                    <p className="mt-2 text-sm text-red-600">{errors.memberStatus.message}</p>
                  )}
                </div>
              </div>
            )}

            {/* Price Display - only show for manual selection */}
            {!preSelectedMembership && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-sm text-gray-600">Selected Membership</div>
                    <div className="font-semibold text-gray-900">
                      {membershipType === 'household' ? 'Household' : 'Single/Senior'} - {memberStatus === 'new' ? 'New Member' : 'Returning Member'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600">Total Amount</div>
                    <div className="text-3xl font-bold text-blue-600">${calculatePrice()}</div>
                    {memberStatus === 'new' && (
                      <div className="text-xs text-gray-500">Includes $500 initiation fee</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Primary Contact Information */}
          <div className="card p-8">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Primary Contact Information</h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('firstName', {
                    required: 'First name is required',
                    minLength: { value: 2, message: 'First name must be at least 2 characters' }
                  })}
                  className="input"
                  placeholder="John"
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('lastName', {
                    required: 'Last name is required',
                    minLength: { value: 2, message: 'Last name must be at least 2 characters' }
                  })}
                  className="input"
                  placeholder="Smith"
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  className="input"
                  placeholder="john.smith@example.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  {...register('phone', {
                    required: 'Phone number is required',
                    pattern: {
                      value: /^[\d\s\-\(\)]+$/,
                      message: 'Invalid phone number'
                    }
                  })}
                  className="input"
                  placeholder="(770) 555-1234"
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Date of Birth <span className="text-red-500">*</span>
                </label>
                <DatePicker
                  selected={primaryDateOfBirth}
                  onChange={(date) => {
                    if (date) {
                      // Set to noon to prevent timezone offset from shifting the day
                      const normalized = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0)
                      setPrimaryDateOfBirth(normalized)
                    } else {
                      setPrimaryDateOfBirth(null)
                    }
                  }}
                  dateFormat="MM/dd/yyyy"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  yearDropdownItemNumber={100}
                  scrollableYearDropdown
                  maxDate={new Date()}
                  placeholderText="MM/DD/YYYY"
                  className="input w-full"
                  required
                />
                {!primaryDateOfBirth && errors.dateOfBirth && (
                  <p className="mt-1 text-sm text-red-600">Date of birth is required</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Street Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('address', {
                    required: 'Address is required',
                    minLength: { value: 5, message: 'Address must be at least 5 characters' }
                  })}
                  className="input"
                  placeholder="123 Main Street"
                />
                {errors.address && (
                  <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('city', { required: 'City is required' })}
                  className="input"
                  placeholder="Peachtree Corners"
                />
                {errors.city && (
                  <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  {...register('state', { required: 'State is required' })}
                  className="input"
                >
                  <option value="">Select State</option>
                  <option value="GA">Georgia</option>
                  <option value="AL">Alabama</option>
                  <option value="FL">Florida</option>
                  <option value="NC">North Carolina</option>
                  <option value="SC">South Carolina</option>
                  <option value="TN">Tennessee</option>
                </select>
                {errors.state && (
                  <p className="mt-1 text-sm text-red-600">{errors.state.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  ZIP Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('zipCode', {
                    required: 'ZIP code is required',
                    pattern: {
                      value: /^\d{5}(-\d{4})?$/,
                      message: 'Invalid ZIP code'
                    }
                  })}
                  className="input"
                  placeholder="30360"
                />
                {errors.zipCode && (
                  <p className="mt-1 text-sm text-red-600">{errors.zipCode.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Household Members (for household membership or senior with household) */}
          {showHouseholdMembers && (
            <div className="card p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Household Members</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Add all family members who will be using the facilities
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addHouseholdMember}
                  className="btn btn-outline text-sm"
                >
                  Add Member
                </button>
              </div>

              <div className="space-y-6">
                {householdMembers.map((member, index) => (
                  <div key={index} className="p-6 border-2 border-gray-200 rounded-lg relative">
                    {householdMembers.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeHouseholdMember(index)}
                        className="absolute top-4 right-4 text-red-600 hover:text-red-800"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                    <h3 className="font-semibold text-gray-900 mb-4">Member {index + 1}</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={member.firstName}
                          onChange={(e) => updateHouseholdMember(index, 'firstName', e.target.value)}
                          className="input"
                          placeholder="First name"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={member.lastName}
                          onChange={(e) => updateHouseholdMember(index, 'lastName', e.target.value)}
                          className="input"
                          placeholder="Last name"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address <span className="text-gray-500 text-xs">(optional)</span>
                        </label>
                        <input
                          type="email"
                          value={member.email}
                          onChange={(e) => updateHouseholdMember(index, 'email', e.target.value)}
                          className="input"
                          placeholder="email@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Date of Birth <span className="text-red-500">*</span>
                        </label>
                        <DatePicker
                          selected={member.dateOfBirth ? new Date(member.dateOfBirth + 'T12:00:00') : null}
                          onChange={(date) => {
                            if (date) {
                              // Use local date parts to avoid UTC day-shift
                              const y = date.getFullYear()
                              const m = String(date.getMonth() + 1).padStart(2, '0')
                              const d = String(date.getDate()).padStart(2, '0')
                              updateHouseholdMember(index, 'dateOfBirth', `${y}-${m}-${d}`)
                            } else {
                              updateHouseholdMember(index, 'dateOfBirth', '')
                            }
                          }}
                          dateFormat="MM/dd/yyyy"
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          yearDropdownItemNumber={100}
                          scrollableYearDropdown
                          maxDate={new Date()}
                          placeholderText="Select date"
                          className="input w-full"
                          required
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}


          {/* Emergency Contact */}
          <div className="card p-8">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Emergency Contact</h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Emergency Contact Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('emergencyContactName', {
                    required: 'Emergency contact name is required',
                    minLength: { value: 2, message: 'Name must be at least 2 characters' }
                  })}
                  className="input"
                  placeholder="Jane Doe"
                />
                {errors.emergencyContactName && (
                  <p className="mt-1 text-sm text-red-600">{errors.emergencyContactName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Emergency Contact Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  {...register('emergencyContactPhone', {
                    required: 'Emergency contact phone is required',
                    pattern: {
                      value: /^[\d\s\-\(\)]+$/,
                      message: 'Invalid phone number'
                    }
                  })}
                  className="input"
                  placeholder="(770) 555-5678"
                />
                {errors.emergencyContactPhone && (
                  <p className="mt-1 text-sm text-red-600">{errors.emergencyContactPhone.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Relationship <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('emergencyContactRelationship', { required: 'Relationship is required' })}
                  className="input"
                  placeholder="Spouse, Parent, Friend, etc."
                />
                {errors.emergencyContactRelationship && (
                  <p className="mt-1 text-sm text-red-600">{errors.emergencyContactRelationship.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Terms and Conditions */}
          <div className="card p-8">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Terms and Agreement</h2>

            {/* Rules and Regulations */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Club Rules and Regulations</h3>
              <p className="text-sm text-gray-600 mb-3">
                Please read the following rules and regulations carefully. By submitting this application,
                you agree to abide by all club rules and regulations.
              </p>
              <div className="bg-gray-50 border border-gray-300 rounded-lg p-6 max-h-96 overflow-y-auto">
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap font-sans">
{`Lockridge Forest Swim & Tennis Club, Inc.
Rules and Regulations

The following Rules and Regulations are for the protection and benefit of all Members. Each Member is responsible for understanding the rules and ensuring their children and guests understand them. Questions regarding rules interpretation and their enforcement should be forwarded to the Board of Directors at www.lockridgeforestswimtennis.com under the Contact tab. Your suggestions and recommendations are encouraged and welcomed. Please avoid confrontations with responsible officers and employees in their enforcement of these rules.

Club Use Liability Waiver:
The Club assumes no responsibility, and Members or their guests shall have no claims against the Club, for any accident or injury to any person or their property. All persons using the Club's facilities do so at their own risk. Only Members who have paid their dues in full and remain in good standing and authorized guests shall be permitted to use the Club's facilities. The Club Use Liability Waiver is documented in the Club Bylaws paragraph 6.10 is in effect for all persons using the club facilities.

Important – USE THE FACILITY / POOL AT YOUR OWN RISK.

INDEX
• COMMON CLUB RULES AND REGULATIONS
• POOL HOURS
• POOL GUEST(s)
• GET TOGETHERS AND PARTIES
• SWIMMING POOL - GENERAL RULES
• SWIMMING POOL - SAFETY RULES
• SWIM AT YOUR OWN RISK
• WADING POOL
• SWIMMING POOL - HEALTH RULES
• OUTDOOR FIREPLACE USE
• PAVILLION / TAJ MAHAL RESERVATION AND GUIDELINES
• TENNIS COURT RULES
• GOVERNANCE

COMMON CLUB RULES AND REGULATIONS

NOTE: Residents of Lockridge Forest who are not members of the Swim & Tennis Club and any other non-members are not permitted, other than member guests, to use any part of the facility or attend any function held at the facility without permission by a majority vote of Board members.

A member is anyone who resides in a household for which the appropriate dues have been paid. A guest is anyone visiting a paid household, or an individual who lives outside of Lockridge Forest and has been invited by a member to join them using the facility. Persons living in Lockridge Forest who are not part of a dues paying household cannot be guests without prior approval of the Board.

1. Club operating hours are Sunrise to Sunset except for scheduled events, functions where a facility has been reserved by a member and approved by the Board, and Swimming and Tennis which have separate posted hours. All other members must leave the facility at sunset. No loitering on Club property after closing hours.

2. Relatives and friends are permitted to use the facilities at any time while accompanied by a member.

3. For the purpose of Club Rules an adult is anyone 18 years old or older.

4. Profane or abusive language, gross behavior, or unsafe conduct which in the opinion of the Pool Manager, any Board member, or lifeguard on duty is unacceptable or unsafe may result in the temporary or permanent suspension of that Member or child.

5. Consumption of alcoholic beverages on Club property is a matter of personal preference. A person showing signs of intoxication may be asked to leave the Club facilities and is subject to suspension and/or expulsion. No person younger than 21 years old shall be allowed to bring or consume any alcoholic beverage on any part of the club's premises.

6. Any person(s) using illegal drugs on Club property may be reported to the proper authorities and subject to immediate suspension and/or expulsion from the CLUB.

7. All trash, including cigarettes, is to be discarded into a proper disposal container. Large items such as pizza boxes, cake boxes, etc. should be discarded directly in the dumpster to minimize bees and hornets around trash receptacles. Trash receptacles are to be emptied when full and receptacles relined for next use. Take trash to the dumpster for discard.

8. Maximum vehicle speed on the Club property is 10MPH.

9. Licensed vehicles, including motorcycles and minibikes, operated by licensed drivers including unlicensed vehicles such as golf carts can park in authorized club parking area. Do not park on the road, parking lot shoulders, or grass at any time.

10. In general, vehicles are not permitted to use the Taj as a parking area, with the exception of golf carts, vehicles transporting a handicapped person(s), and individual vehicles needed to drop off or pickup supplies for an event.

11. Bicycles, when parked, must be in a bike rack if provided. If no bike rack is provided, the designated bike parking area is adjacent to the main pool entry gate.

12. No person shall take any article belonging to the Club away from the Club premises without prior approval from a board member.

13. Unleashed dogs are allowed on Club property only with attentive owners. If members present are uncomfortable with the dog(s) being off leash, a leash must be used. All persons with dogs on club property are responsible for cleaning up after them. Please take extra caution when large groups or children are present. Many small children are frightened of dogs of all sizes. No dogs allowed on the pool deck area.

14. A Member who damages or defaces Club property will be responsible for the repair or replacement of the same. In the case of children, parents will be responsible for all costs. In the case of guests, the Member is ultimately responsible for all costs.

15. Climbing over the pool enclosure fences is forbidden and may result in suspension of pool privileges.

16. Security of the CLUB Property is the responsibility of ALL Members. Members must abide by the rules regarding locking the tennis courts after use, locking the main pool gate (last to leave pool), and locking the property entry gate at the top of the hill (last vehicle leaving property regardless of time of day).

For complete rules including POOL HOURS, POOL GUESTS, SWIMMING POOL RULES, TENNIS COURT RULES, and GOVERNANCE, please visit www.lockridgeforestswimtennis.com.`}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  {...register('agreeToRules', { required: 'You must agree to the club rules' })}
                  className="mt-1 mr-3 w-5 h-5"
                />
                <span className="text-gray-700">
                  I agree to abide by all club rules, regulations, and bylaws as established by
                  Lockridge Forest Swim Tennis Club. <span className="text-red-500">*</span>
                </span>
              </label>
              {errors.agreeToRules && (
                <p className="ml-8 text-sm text-red-600">{errors.agreeToRules.message}</p>
              )}

              <label className="flex items-start">
                <input
                  type="checkbox"
                  {...register('agreeToCommunications', { required: 'You must agree to receive communications' })}
                  className="mt-1 mr-3 w-5 h-5"
                />
                <span className="text-gray-700">
                  I agree to receive important communications from the club regarding membership,
                  events, and facility updates. <span className="text-red-500">*</span>
                </span>
              </label>
              {errors.agreeToCommunications && (
                <p className="ml-8 text-sm text-red-600">{errors.agreeToCommunications.message}</p>
              )}

              <label className="flex items-start">
                <input
                  type="checkbox"
                  {...register('agreeTolLiability', { required: 'You must agree to the liability waiver' })}
                  className="mt-1 mr-3 w-5 h-5"
                />
                <span className="text-gray-700">
                  I understand and accept the liability waiver and acknowledge that the use of club
                  facilities is at my own risk. <span className="text-red-500">*</span>
                </span>
              </label>
              {errors.agreeTolLiability && (
                <p className="ml-8 text-sm text-red-600">{errors.agreeTolLiability.message}</p>
              )}
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-outline"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </span>
              ) : (
                'Continue to Payment'
              )}
            </button>
          </div>

          {/* Help Text */}
          <div className="text-center text-sm text-gray-600">
            <p>Questions? Contact us at admin@lockridgeforestswimtennis.com or (770) 609-7428</p>
          </div>
        </form>
      </div>
    </div>
  )
}

export default MembershipForm
